import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { essentialsApi, type EntryRequirement, type RequirementsResponse, type VisaStatus } from '../../../api/essentials.api';
import { CountrySelect } from '../../../components/ui/CountrySelect';
import { RequirementCardSkeleton } from './InfoSkeletons';
import { useAuthStore } from '../../../stores/auth.store';
import {
  Icon, Stamp, Syringe, Pill, BookUser, ExternalLink, Info, Users, type LucideIcon,
} from '../../../components/ui/Icon';
import InfoSectionHeader from './InfoSectionHeader';

// Dot + text, not a filled pill: the status reads as a verdict, not a tag.
const VISA_TONE: Record<VisaStatus, { dot: string; text: string }> = {
  visa_free:       { dot: 'bg-green-500', text: 'text-green-700 dark:text-green-400' },
  eta:             { dot: 'bg-sky-500',   text: 'text-sky-700 dark:text-sky-400' },
  e_visa:          { dot: 'bg-amber-500', text: 'text-amber-700 dark:text-amber-400' },
  visa_on_arrival: { dot: 'bg-amber-500', text: 'text-amber-700 dark:text-amber-400' },
  visa_required:   { dot: 'bg-orange-500', text: 'text-orange-700 dark:text-orange-400' },
  no_admission:    { dot: 'bg-coral-500', text: 'text-coral-700 dark:text-red-400' },
};

const LABEL = 'font-mono text-[10px] tracking-label uppercase text-slate-400 dark:text-slate-500';

function Fact({ icon, label, children }: { icon: LucideIcon; label: string; children: React.ReactNode }) {
  return (
    <div className="px-5 py-4 min-w-0">
      <p className={`flex items-center gap-1.5 mb-2 ${LABEL}`}>
        <Icon as={icon} size={12} /> {label}
      </p>
      {children}
    </div>
  );
}

function RequirementCard({ req }: { req: EntryRequirement }) {
  const { t, i18n } = useTranslation();
  const checked = new Date(req.checkedAt).toLocaleDateString(i18n.language, { day: 'numeric', month: 'short', year: 'numeric' });
  const tone = req.visa ? VISA_TONE[req.visa.status] : null;

  return (
    <article className="card overflow-hidden">
      <header className="flex items-center gap-3 px-5 py-4 border-b border-slate-100 dark:border-slate-800">
        <span className="w-10 h-10 shrink-0 rounded-full flex items-center justify-center bg-canvas dark:bg-slate-800 text-xl leading-none">
          {req.nationalityFlag ?? '🌍'}
        </span>
        <div className="min-w-0">
          <h4 className="font-serif text-lg leading-snug text-slate-900 dark:text-slate-100">
            {t('essentials.requirements.forNationality', { nationality: req.nationalityName })}
          </h4>
          {req.travellers.length > 0 && (
            <p className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400 truncate">
              <Icon as={Users} size={12} className="shrink-0" /> {req.travellers.join(' · ')}
            </p>
          )}
        </div>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1.4fr] divide-y md:divide-y-0 md:divide-x divide-slate-100 dark:divide-slate-800">
        <Fact icon={Stamp} label={t('essentials.requirements.visa')}>
          {req.visa && tone ? (
            <>
              <p className={`flex items-center gap-2 font-serif text-xl leading-tight ${tone.text}`}>
                <span className={`w-2 h-2 rounded-full shrink-0 ${tone.dot}`} />
                {t(`essentials.visa.${req.visa.status}`)}
              </p>
              {req.visa.days ? (
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{t('essentials.visa.upToDays', { count: req.visa.days })}</p>
              ) : null}
            </>
          ) : (
            <p className="text-sm text-slate-400 italic">{t('essentials.requirements.unknown')}</p>
          )}
        </Fact>

        <Fact icon={BookUser} label={t('essentials.requirements.passport')}>
          <p className="text-sm leading-relaxed text-slate-700 dark:text-slate-300">
            {req.passportValidityMonths > 0
              ? t('essentials.requirements.passportValidity', { count: req.passportValidityMonths })
              : t('essentials.requirements.passportValidZero')}
          </p>
        </Fact>

        <Fact icon={Syringe} label={t('essentials.requirements.vaccines')}>
          {req.vaccinesRequired.length === 0 && req.vaccinesRecommended.length === 0 ? (
            <p className="text-sm text-slate-600 dark:text-slate-400">{t('essentials.requirements.noVaccines')}</p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {req.vaccinesRequired.map(v => (
                <span key={v} className="px-2.5 py-1 rounded-full text-xs font-medium border border-coral-500/20 bg-coral-50 text-coral-700 dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
                  {v} · {t('essentials.requirements.mandatory')}
                </span>
              ))}
              {req.vaccinesRecommended.map(v => (
                <span key={v} className="px-2.5 py-1 rounded-full text-xs font-medium border border-sand-100 bg-sand-50 text-sand-800 dark:bg-amber-900/20 dark:border-amber-900/40 dark:text-amber-200">
                  {v}
                </span>
              ))}
            </div>
          )}
          {req.healthNotes && (
            <p className="flex items-start gap-1.5 mt-2.5 text-xs leading-relaxed text-slate-500 dark:text-slate-400">
              <Icon as={Pill} size={12} className="mt-0.5 shrink-0" /> {req.healthNotes}
            </p>
          )}
        </Fact>
      </div>

      <footer className="flex items-center justify-between gap-2 px-5 py-3 border-t border-slate-100 dark:border-slate-800 bg-canvas/60 dark:bg-slate-800/30">
        <span className={LABEL}>{t('essentials.requirements.checkedOn', { date: checked })}</span>
        <a
          href={req.officialUrl} target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-xs font-semibold text-teal-700 dark:text-teal-400 hover:text-teal-800 dark:hover:text-teal-300 transition-colors"
        >
          {t('essentials.requirements.verifyOfficial')} <Icon as={ExternalLink} size={12} />
        </a>
      </footer>
    </article>
  );
}

export default function EntryRequirementsPanel({ tripId }: { tripId: string }) {
  const { t, i18n } = useTranslation();
  const user = useAuthStore(s => s.user);
  const [countries, setCountries] = useState<RequirementsResponse['countries']>([]);
  const [missing, setMissing]     = useState(false);
  const [loading, setLoading]     = useState(true);
  const [picker, setPicker]       = useState(user?.nationalityCode ?? '');
  const [saving, setSaving]       = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    essentialsApi.requirements(tripId, i18n.language)
      .then(r => { setCountries(r.countries); setMissing(r.missingNationality); })
      .catch(() => { /* non-blocking */ })
      .finally(() => setLoading(false));
  }, [tripId, i18n.language]);

  useEffect(() => { load(); }, [load]);

  async function saveNationality() {
    if (!picker) return;
    setSaving(true);
    try {
      await essentialsApi.setNationality(tripId, picker);
      load();
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="mb-10">
      <InfoSectionHeader icon={<Icon as={Stamp} size={17} />} title={t('essentials.requirements.heading')} />

      {loading ? (
        <RequirementCardSkeleton />
      ) : (<>
        {missing && (
          <div className="rounded-2xl border border-dashed border-teal-300 dark:border-teal-800 bg-teal-50/50 dark:bg-teal-900/10 p-4 mb-4">
            <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2.5">{t('essentials.requirements.missingPrompt')}</p>
            <div className="flex items-stretch gap-2 max-w-sm">
              <div className="flex-1"><CountrySelect value={picker} onChange={setPicker} /></div>
              <button
                onClick={saveNationality}
                disabled={!picker || saving}
                className="shrink-0 px-4 rounded-xl bg-teal-500 hover:bg-teal-600 text-white text-sm font-semibold transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {saving ? t('common.saving') : t('common.save')}
              </button>
            </div>
          </div>
        )}

        {/* ADR-0030: each country the trip enters has its own rules. */}
        {countries.filter(c => c.requirements.length > 0).map(c => (
          <div key={c.country.code} className="mb-4">
            {countries.length > 1 && (
              <h4 className="font-mono text-[10px] tracking-label uppercase text-slate-400 mb-2">
                {t('essentials.requirements.forCountry', { country: c.country.name ?? c.country.code })}
              </h4>
            )}
            <div className="space-y-4">
              {c.requirements.map(r => <RequirementCard key={r.nationality} req={r} />)}
            </div>
          </div>
        ))}

        {!countries.some(c => c.requirements.length > 0) && !missing && (
          <p className="text-sm text-slate-400 italic">{t('essentials.requirements.empty')}</p>
        )}
      </>)}

      <p className="flex items-start gap-1.5 mt-3 max-w-3xl text-[11px] leading-relaxed text-slate-400 dark:text-slate-500">
        <Icon as={Info} size={12} className="mt-px shrink-0" />
        {t('essentials.requirements.disclaimer')}
      </p>
    </section>
  );
}
